import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../../state/refresh'

interface OrderSummaryProps {
  plan?: string
  price?: string
  currency?: string
  frequency?: string
  usersCount?: number
}

const OrderSummary = () => {
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const [orderDetails, setOrderDetails] = useState<OrderSummaryProps>({})
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const requestMaker = useRequests()
  const dispatch = useDispatch()

  useEffect(() => {
    // Pobieranie danych z locationState lub ustawianie domyślnych
    if (location.state) {
      setOrderDetails(location.state)
    } else {
      navigate('/pricing')
    }
  }, [location, navigate])

  const handleConfirmOrder = async () => {
    // Zapobiegaj wielokrotnemu kliknięciu
    if (isProcessing) return

    setIsProcessing(true)

    try {
      // Sprawdź czy plan został wybrany
      if (!orderDetails.plan) {
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Nie wybrano pakietu'
          })
        )
        setIsProcessing(false)
        return
      }

      // Oblicz datę wygaśnięcia pakietu na podstawie częstotliwości
      let packageExpiredAt: Date | undefined

      if (orderDetails.frequency === '/miesiąc' || orderDetails.frequency === '/30 dni') {
        packageExpiredAt = new Date()
        packageExpiredAt.setMonth(packageExpiredAt.getMonth() + 1)
      } else if (orderDetails.frequency === '/rok') {
        packageExpiredAt = new Date()
        packageExpiredAt.setFullYear(packageExpiredAt.getFullYear() + 1)
      }

      // Przygotuj opis płatności
      const description = `Zakup pakietu ${orderDetails.plan} w serwisie Kreatlo`

      // Przygotuj kwotę jako liczba
      const amount = parseFloat(orderDetails.price || '0')

      // Wywołaj API createPayment
      const response = await requestMaker.createPayment({
        package: orderDetails.plan || '',
        amount,
        description,
        packageExpiredAt,
        packageUsersNumber: orderDetails.usersCount
      })

      // Jeśli otrzymamy URL do płatności, przekieruj użytkownika
      if (response.data && response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl
      } else {
        throw new Error('Nie otrzymano URL do płatności')
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Wystąpił błąd podczas tworzenia płatności'
        })
      )
      setIsProcessing(false)
    }
  }

  return (
    <section className='order-summary pt-50 pb-70'>
      <div className='container'>
        <div className='row'>
          <div className='col-sm-12'>
            <div className='section-heading text-center mb-40'>
              <h2 className='heading__subtitle'>Twoje Zamówienie</h2>
              <h3 className='heading__title'>Podsumowanie Zamówienia</h3>
            </div>
          </div>
        </div>

        {!isConfirmed
          ? (
            <div className='row justify-content-center'>
              <div className='col-sm-12 col-md-8 col-lg-6'>
                <div className='card'>
                  <div className='card-body'>
                    <h5 className='card-title text-center mb-30'>Szczegóły zamówienia</h5>

                    <div className='order-details'>
                      <div className='detail-row d-flex justify-content-between mb-10'>
                        <span className='detail-label'>Wybrany pakiet:</span>
                        <span className='detail-value font-weight-bold'>{orderDetails.plan}</span>
                      </div>
                      <div className='detail-row d-flex justify-content-between mb-10'>
                        <span className='detail-label'>Liczba użytkowników:</span>
                        <span className='detail-value font-weight-bold'>{orderDetails.usersCount}</span>
                      </div>
                      <div className='detail-row d-flex justify-content-between mb-10'>
                        <span className='detail-label'>Okres:</span>
                        <span className='detail-value'>
                          {orderDetails.frequency === '/miesiąc' || orderDetails.frequency === '/30 dni'
                            ? 'Miesięczny'
                            : 'Roczny'}
                        </span>
                      </div>
                      <div className='detail-row d-flex justify-content-between mb-10'>
                        <span className='detail-label'>Cena (zawiera VAT 23%):</span>
                        <span className='detail-value'>{orderDetails.currency} {orderDetails.price} {orderDetails.frequency}</span>
                      </div>
                      <hr className='my-20' />
                      <div className='detail-row d-flex justify-content-between mb-10 font-weight-bold'>
                        <span className='detail-label'>Suma do zapłaty:</span>
                        <span className='detail-value'>{orderDetails.price && orderDetails.currency
                          ? `${orderDetails.currency} ${orderDetails.price}`
                          : ''}
                        </span>
                      </div>
                    </div>

                    <div className='payment-info mt-30 text-center'>
                      <p className='mb-10'>Po kliknięciu przycisku zostaniesz przekierowany do bramki płatności AutoPay</p>
                    </div>

                    <div className='actions mt-40 text-center'>
                      <button
                        className={`
                          tw-w-full tw-mb-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                          tw-text-white
                          tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                          tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                        `}
                        onClick={() => navigate('/pricing')}
                      >
                        Wróć
                      </button>
                      <button
                        className={`
                          tw-w-full tw-mb-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                          tw-text-white
                          tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                          tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                        `}
                        onClick={handleConfirmOrder}
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Przetwarzanie...' : 'Przejdź do płatności'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )
          : (
            <div className='row justify-content-center'>
              <div className='col-sm-12 col-md-8 col-lg-6'>
                <div className='card order-confirmed'>
                  <div className='card-body text-center'>
                    <div className='icon-box mb-30'>
                      <span className='icon-check-circle confirmation-icon text-success' style={{ fontSize: '4rem' }} />
                    </div>
                    <h4 className='card-title mb-20'>Zamówienie zostało złożone!</h4>
                    <p className='card-text mb-30'>
                      Dziękujemy za zamówienie pakietu <strong>{orderDetails.plan}</strong>.
                      Na Twój adres email wysłaliśmy potwierdzenie z szczegółami zamówienia.
                    </p>
                    <p className='mb-30'>
                      Numer zamówienia: <strong>ORD-{Math.floor(100000 + Math.random() * 900000)}</strong>
                    </p>
                    <div className='actions mt-20'>
                      <button className='btn btn__primary' onClick={() => navigate('/')}>
                        Wróć do strony głównej
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
      </div>
    </section>
  )
}

export default OrderSummary
